import { useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  ArrowLeft, Camera, Plus, ChevronRight, ClipboardList, CheckCircle2,
  AlertTriangle, Clock, Share2,
} from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useRequireAuth, hasRole } from "@/lib/auth";
import { Card, Pill, Button, Spinner, EmptyState } from "@/components/ui";
import { format, parseISO } from "date-fns";
import HopShareModal from "./HopShareModal";

// =============================================================================
// HopPacksListScreen — /projects/:projectId/hop
// Library landing for a project's HOP packs. Any team member (manager+ or
// is_on_project) can browse and open a pack; only manager+ see the "New pack"
// CTA to create one. T-010 Commit C.1.
// =============================================================================

interface Pack {
  pack_id:      string;
  pack_number:  string;
  title:        string | null;
  status:       "open" | "submitted" | "accepted" | "defects_raised";
  submitted_at: string | null;
  accepted_at:  string | null;
  created_at:   string;
  template?:    { template_code: string; display_name: string } | null;
}

interface ProjectMini {
  project_id:   string;
  project_code: string;
  project_name: string;
  status:       string;
  actual_end_date:        string | null;
  acceptance_received_at: string | null;
}

// HOP status → Pill Tone. Sticking to the central Tone union so typecheck
// stays clean (Pill only accepts specific tones).
const PACK_TONE: Record<Pack["status"], "open" | "pending" | "approved" | "rejected"> = {
  open:           "open",
  submitted:      "pending",
  accepted:       "approved",
  defects_raised: "rejected",
};

const PACK_LABEL: Record<Pack["status"], string> = {
  open:           "in progress",
  submitted:      "awaiting review",
  accepted:       "accepted",
  defects_raised: "defects raised",
};

export default function HopPacksListScreen() {
  const { projectId } = useParams<{ projectId: string }>();
  const user = useRequireAuth();
  const navigate = useNavigate();

  const canCreate = hasRole(user, "manager");
  const [portalOpen, setPortalOpen] = useState(false);

  // Project context — keeps the header informative across the back-button
  // journey from the field project detail.
  const { data: project, refetch: refetchProject } = useQuery({
    queryKey: ["hop_project_mini", projectId],
    enabled: !!projectId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("projects")
        .select("project_id, project_code, project_name, status, actual_end_date, acceptance_received_at")
        .eq("project_id", projectId!)
        .maybeSingle();
      if (error) throw error;
      return data as ProjectMini | null;
    },
  });

  // Slice 2 — HOP gates gently nudging the project status. Suggestion only, one
  // click, never forced (a project may have other closeout criteria beyond HOP).
  const [statusBusy, setStatusBusy] = useState(false);
  const [dismissedSuggest, setDismissedSuggest] = useState(false);
  async function applyProjectStatus(target: string) {
    if (!projectId) return;
    setStatusBusy(true);
    try {
      const patch: any = { status: target };
      // Closing a project requires an acceptance date + end date (DB check
      // constraint) — the same fields the Change-status→Close flow sets. A bare
      // status write was being rejected, which is why this button did nothing.
      // Default acceptance/end to today (editable later via Edit dates).
      if (target === "closed") {
        const today = new Date().toISOString().slice(0, 10);
        if (!project?.actual_end_date)        patch.actual_end_date = today;
        if (!project?.acceptance_received_at) patch.acceptance_received_at = new Date().toISOString();
      }
      const { error } = await supabase.from("projects").update(patch).eq("project_id", projectId);
      if (error) { alert(`Couldn't update the project status: ${error.message}`); return; }
      await refetchProject();
    } finally { setStatusBusy(false); }
  }

  const { data: packs = [], isLoading } = useQuery({
    queryKey: ["hop_packs", projectId],
    enabled: !!projectId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("hop_packs")
        .select(`
          pack_id, pack_number, title, status,
          submitted_at, accepted_at, created_at,
          template:template_id(template_code, display_name)
        `)
        .eq("project_id", projectId!)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as unknown as Pack[];
    },
  });

  // Per-pack progress — how many of the pack's categories have at least one
  // photo, plus the total photo count. Drives the progress bar + "ready to
  // submit" cue so field + managers can see at a glance what's outstanding.
  const { data: progress = {} } = useQuery({
    queryKey: ["hop_packs_progress", projectId, packs.length],
    enabled: !!projectId && packs.length > 0,
    queryFn: async () => {
      const packIds = packs.map(p => p.pack_id);
      const [catsRes, assetsRes] = await Promise.all([
        supabase.from("hop_pack_categories").select("pack_id, pack_category_id").in("pack_id", packIds),
        supabase.from("hop_assets").select("pack_id, pack_category_id").in("pack_id", packIds).eq("asset_type", "photo"),
      ]);
      const totalCats = new Map<string, Set<string>>();
      for (const c of (catsRes.data ?? []) as any[]) {
        if (!totalCats.has(c.pack_id)) totalCats.set(c.pack_id, new Set());
        totalCats.get(c.pack_id)!.add(c.pack_category_id);
      }
      const filledCats = new Map<string, Set<string>>();
      const photos = new Map<string, number>();
      for (const a of (assetsRes.data ?? []) as any[]) {
        photos.set(a.pack_id, (photos.get(a.pack_id) ?? 0) + 1);
        if (a.pack_category_id) {
          if (!filledCats.has(a.pack_id)) filledCats.set(a.pack_id, new Set());
          filledCats.get(a.pack_id)!.add(a.pack_category_id);
        }
      }
      const out: Record<string, { total: number; filled: number; photos: number }> = {};
      for (const id of packIds) out[id] = { total: totalCats.get(id)?.size ?? 0, filled: filledCats.get(id)?.size ?? 0, photos: photos.get(id) ?? 0 };
      return out;
    },
  });

  // Group by status for clearer scanning
  const groups = useMemo(() => {
    const open:     Pack[] = [];
    const review:   Pack[] = [];
    const done:     Pack[] = [];
    const defects:  Pack[] = [];
    for (const p of packs) {
      if (p.status === "open")            open.push(p);
      else if (p.status === "submitted")  review.push(p);
      else if (p.status === "accepted")   done.push(p);
      else                                 defects.push(p);
    }
    return { open, review, done, defects };
  }, [packs]);

  // What HOP is implying the project status should be. Never auto-applied.
  const suggestion = useMemo(() => {
    if (!project || packs.length === 0) return null;
    const st = project.status;
    const allAccepted = packs.every(p => p.status === "accepted");
    const anySubmitted = packs.some(p => p.status === "submitted");
    if (allAccepted && st !== "closed" && st !== "cancelled" && st !== "lost")
      return { target: "closed", label: "Closed", reason: "Every HOP pack on this project is accepted." };
    if (anySubmitted && (st === "mobilising" || st === "live"))
      return { target: "acceptance_pending", label: "Acceptance pending", reason: "A HOP pack is submitted and awaiting client sign-off." };
    return null;
  }, [project, packs]);

  if (!user) return null;

  return (
    <div className="p-4 md:p-6 max-w-[1000px] mx-auto">
      <button
        onClick={() => navigate(`/projects/${projectId}`)}
        className="flex items-center gap-1.5 text-[12px] text-text-muted hover:text-text mb-3"
        type="button"
      >
        <ArrowLeft size={14} /> Project
      </button>

      <div className="mb-5 flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          {project && (
            <div className="text-[11px] font-mono text-text-muted">
              {project.project_code} · {project.project_name}
            </div>
          )}
          <h1 className="text-[22px] md:text-2xl font-semibold tracking-tight flex items-center gap-2">
            <Camera size={20} className="text-accent" />
            HOP library
          </h1>
          <div className="text-text-muted text-[13px] mt-1">
            Handover-of-pack bundles for this project. Photos, RLMs, cable sweeps — all bucketed by category.
          </div>
        </div>
        {canCreate && (
          <div className="flex items-center gap-2 flex-shrink-0">
            <Button variant="secondary" icon={Share2} onClick={() => setPortalOpen(true)}>Client portal</Button>
            <Link to={`/projects/${projectId}/hop/new`}>
              <Button icon={Plus}>New HOP pack</Button>
            </Link>
          </div>
        )}
      </div>

      {/* Slice 2 — one-click project-status nudge off the HOP gates (manager+). */}
      {canCreate && suggestion && !dismissedSuggest && (
        <div className="mb-5 rounded-lg border border-accent/40 bg-accent/5 p-3.5 flex items-start gap-3 flex-wrap">
          <CheckCircle2 size={16} className="text-accent mt-0.5 flex-shrink-0" />
          <div className="flex-1 min-w-[220px]">
            <div className="text-[13px] text-text">{suggestion.reason}</div>
            <div className="text-[12px] text-text-muted mt-0.5">
              Move this project to <span className="text-text font-medium">{suggestion.label}</span>? This is only a suggestion — nothing changes unless you click.
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <Button size="sm" variant="secondary" onClick={() => setDismissedSuggest(true)} disabled={statusBusy}>Not yet</Button>
            <Button size="sm" onClick={() => applyProjectStatus(suggestion.target)} disabled={statusBusy}>
              {statusBusy ? "Updating…" : `Mark ${suggestion.label}`}
            </Button>
          </div>
        </div>
      )}

      {portalOpen && projectId && (
        <HopShareModal projectId={projectId} onClose={() => setPortalOpen(false)} />
      )}

      {isLoading && (
        <div className="py-16 flex justify-center"><Spinner size={20} /></div>
      )}

      {!isLoading && packs.length === 0 && (
        <Card>
          <EmptyState
            title="No HOP packs yet"
            description={canCreate
              ? "Start a pack from a template (NBN, HVAC, CUSTOM). Crew can then upload photos and docs against each category."
              : "When a manager creates a HOP pack for this project, you'll see it here and can start uploading."}
            icon={Camera}
          />
          {canCreate && (
            <div className="p-4 text-center">
              <Link to={`/projects/${projectId}/hop/new`}>
                <Button icon={Plus}>Create the first HOP pack</Button>
              </Link>
            </div>
          )}
        </Card>
      )}

      {!isLoading && packs.length > 0 && (
        <div className="space-y-6">
          {groups.open.length > 0 && (
            <Section label="In progress" icon={Clock} packs={groups.open} projectId={projectId!} progress={progress} />
          )}
          {groups.review.length > 0 && (
            <Section label="Awaiting review" icon={ClipboardList} packs={groups.review} projectId={projectId!} progress={progress} />
          )}
          {groups.defects.length > 0 && (
            <Section label="Defects raised" icon={AlertTriangle} packs={groups.defects} projectId={projectId!} progress={progress} />
          )}
          {groups.done.length > 0 && (
            <Section label="Accepted" icon={CheckCircle2} packs={groups.done} projectId={projectId!} progress={progress} muted />
          )}
        </div>
      )}
    </div>
  );
}

function Section({
  label, icon: Icon, packs, projectId, progress, muted,
}: {
  label: string; icon: any; packs: Pack[]; projectId: string;
  progress: Record<string, { total: number; filled: number; photos: number }>; muted?: boolean;
}) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <Icon size={12} className={muted ? "text-text-muted" : "text-accent"} />
        <div className={`text-[11px] tracking-wider2 uppercase font-mono ${muted ? "text-text-muted" : "text-text"}`}>
          {label} · {packs.length}
        </div>
      </div>
      <div className="space-y-2">
        {packs.map(p => <PackRow key={p.pack_id} pack={p} projectId={projectId} prog={progress[p.pack_id]} />)}
      </div>
    </div>
  );
}

function PackRow({ pack, projectId, prog }: { pack: Pack; projectId: string; prog?: { total: number; filled: number; photos: number } }) {
  const tone = PACK_TONE[pack.status];
  const label = PACK_LABEL[pack.status];
  const tplCode = pack.template?.template_code ?? "—";
  const tplName = pack.template?.display_name ?? "Unknown template";
  const timestampLabel =
    pack.status === "accepted"  && pack.accepted_at  ? `accepted ${format(parseISO(pack.accepted_at),  "dd MMM")}` :
    pack.status === "submitted" && pack.submitted_at ? `submitted ${format(parseISO(pack.submitted_at), "dd MMM")}` :
    `started ${format(parseISO(pack.created_at), "dd MMM")}`;

  // Progress only matters while capture is live (open / defects-raised).
  const capturing = pack.status === "open" || pack.status === "defects_raised";
  const total = prog?.total ?? 0;
  const filled = prog?.filled ?? 0;
  const photos = prog?.photos ?? 0;
  const pct = total > 0 ? Math.round((filled / total) * 100) : 0;
  const ready = capturing && total > 0 && filled === total;

  return (
    <Link to={`/projects/${projectId}/hop/${pack.pack_id}`}>
      <Card className="hover:border-line-light transition-colors">
        <div className="p-4 flex items-center gap-3 flex-wrap">
          <div className="w-10 h-10 rounded-md bg-accent/10 text-accent flex items-center justify-center border border-accent/30 flex-shrink-0">
            <Camera size={16} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[11px] font-mono text-text-muted">{pack.pack_number}</span>
              <span className="text-[10px] uppercase tracking-wider2 font-mono text-text-dim">{tplCode}</span>
            </div>
            <div className="text-text mt-0.5">{pack.title || tplName}</div>
            <div className="text-[11px] text-text-muted mt-0.5 flex items-center gap-2 flex-wrap">
              <span>{timestampLabel}</span>
              {photos > 0 && <span className="text-text-dim">· {photos} photo{photos === 1 ? "" : "s"}</span>}
              {capturing && total > 0 && <span className="text-text-dim">· {filled}/{total} categories</span>}
            </div>
            {/* Capture progress bar — live packs only */}
            {capturing && total > 0 && (
              <div className="mt-1.5 h-1 w-full max-w-[220px] rounded-full bg-line overflow-hidden">
                <div className={`h-full rounded-full ${ready ? "bg-ok" : "bg-accent"}`} style={{ width: `${pct}%` }} />
              </div>
            )}
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {ready && <Pill label="ready to submit" tone="approved" small />}
            <Pill label={label} tone={tone} small />
            <ChevronRight size={14} className="text-text-muted" />
          </div>
        </div>
      </Card>
    </Link>
  );
}
